import {
  Speak2MeEnsureChatRequest,
  Speak2MeEnsureChatResponse,
  Speak2MeNeoChatConversationResponse,
  Speak2MeResumeQuery,
  Speak2MeResumeResponse,
  Speak2MeDictationTranscribeResponse,
  VoiceSessionMintRequest,
  VoiceSessionMintResponse,
  VoiceSessionSummary,
  VoiceTtsRequest,
  VoiceTurnLatencyRequest,
} from '@vivport/backend/types';
import { getAccessToken } from '@/utils/token-utils';
import { httpClient } from './http-client';

function voiceRequestHeaders(): Record<string, string> {
  const token = getAccessToken();
  const memberId = localStorage.getItem('memberId');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(memberId ? { 'member-id': memberId } : {}),
  };
}

export async function mintVoiceSession(request: VoiceSessionMintRequest): Promise<VoiceSessionMintResponse> {
  return httpClient.post<VoiceSessionMintResponse, VoiceSessionMintRequest>(
    '/protected/speak2me/voice-session',
    request
  );
}

export async function endVoiceSession(voiceSessionId: string): Promise<VoiceSessionSummary> {
  return httpClient.post<VoiceSessionSummary>(`/protected/speak2me/voice-session/${voiceSessionId}/end`);
}

export async function synthesizeVoiceTts(request: VoiceTtsRequest, signal: AbortSignal): Promise<Response> {
  const post = () =>
    fetch('/api/protected/speak2me/voice-tts', {
      method: 'POST',
      headers: voiceRequestHeaders(),
      body: JSON.stringify(request),
      signal,
    });
  let response: Response;
  try {
    response = await post();
  } catch (err) {
    // Retry once, and only when no response was received (a network TypeError before headers) —
    // the request never reached the backend, so nothing there can have retried it. An HTTP error
    // is NOT retried here: the backend is the single retry owner and has already exhausted its
    // budget closer to the provider, where Retry-After is readable without another round trip.
    // An AbortError is the caller's decision and is never retried either.
    if (err instanceof TypeError) {
      response = await post();
    } else {
      throw err;
    }
  }
  if (!response.ok) {
    throw new Error(`voice-tts failed (HTTP ${response.status})`);
  }
  return response;
}

export async function postVoiceTurnLatency(
  voiceSessionId: string,
  request: VoiceTurnLatencyRequest
): Promise<void> {
  await httpClient.post<void, VoiceTurnLatencyRequest>(
    `/protected/speak2me/voice-session/${voiceSessionId}/turn-latency`,
    request
  );
}

export async function ensureNeoChat(request: Speak2MeEnsureChatRequest = {}): Promise<Speak2MeEnsureChatResponse> {
  return httpClient.post<Speak2MeEnsureChatResponse, Speak2MeEnsureChatRequest>(
    '/protected/speak2me/ensure-chat',
    request
  );
}

export async function resumeSpeak2Me(query: Speak2MeResumeQuery = {}): Promise<Speak2MeResumeResponse> {
  return httpClient.get<Speak2MeResumeResponse>('/protected/speak2me/resume', { params: query });
}

export async function getNeoChatConversation(neoChatId: number): Promise<Speak2MeNeoChatConversationResponse> {
  return httpClient.get<Speak2MeNeoChatConversationResponse>(
    `/protected/speak2me/neo-chat/${neoChatId}/conversation`
  );
}

export async function transcribeDictationAudio(
  audio: Blob,
  options?: { signal?: AbortSignal }
): Promise<Speak2MeDictationTranscribeResponse> {
  const formData = new FormData();
  const filename = audio.type.includes('mp4') ? 'dictation.mp4' : 'dictation.webm';
  formData.append('audio', audio, filename);
  return httpClient.post<Speak2MeDictationTranscribeResponse, FormData>(
    '/protected/speak2me/dictation-transcribe',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...(options?.signal ? { signal: options.signal } : {}),
    }
  );
}
